import { useState, useCallback } from "react";
import { Copy, Download, Code2, FileText, Globe } from "lucide-react";
import { toast } from "sonner";
import { MessageResponse } from "@/components/ai-elements/message";
import { sanitizeSvg } from "@/lib/sanitize";
import type { ArtifactType } from "../types";

interface ArtifactBlockProps {
  id: string;
  title: string;
  description?: string;
  content: string;
  language?: string;
  artifactType: ArtifactType;
}

const TYPE_ICONS: Record<string, React.ElementType> = {
  code: Code2,
  html: Globe,
  svg: Globe,
  document: FileText,
  markdown: FileText,
  csv: FileText,
  json: Code2,
};

function getExtension(type: ArtifactType, language?: string): string {
  if (type === "code" && language) return language;
  const map: Record<string, string> = {
    html: "html", svg: "svg", markdown: "md", csv: "csv", json: "json", document: "txt", code: "txt",
  };
  return map[type] ?? "txt";
}

export function ArtifactBlock({
  title,
  description,
  content,
  language,
  artifactType,
}: ArtifactBlockProps) {
  const [activeTab, setActiveTab] = useState<"preview" | "source">(
    artifactType === "html" || artifactType === "svg" ? "preview" : "source",
  );
  const Icon = TYPE_ICONS[artifactType] ?? FileText;

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(content);
    toast.success("Copied to clipboard");
  }, [content]);

  const handleDownload = useCallback(() => {
    const ext = getExtension(artifactType, language);
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/[^a-zA-Z0-9-_ ]/g, "").replace(/\s+/g, "-").toLowerCase()}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  }, [content, title, artifactType, language]);

  const hasPreview = artifactType === "html" || artifactType === "svg" || artifactType === "markdown";

  return (
    <div className="my-2 overflow-hidden rounded-lg border border-border">
      {/* Header */}
      <div className="flex items-center gap-2 border-b bg-muted/50 px-3 py-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{title}</p>
          {description && (
            <p className="text-xs text-muted-foreground truncate">{description}</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleCopy}
            className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-accent"
            title="Copy"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-accent"
            title="Download"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Tabs (for HTML/SVG with preview) */}
      {hasPreview && (
        <div className="flex border-b bg-muted/30">
          <button
            type="button"
            className={`px-3 py-1.5 text-xs font-medium ${
              activeTab === "preview"
                ? "border-b-2 border-primary text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setActiveTab("preview")}
          >
            Preview
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 text-xs font-medium ${
              activeTab === "source"
                ? "border-b-2 border-primary text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setActiveTab("source")}
          >
            Source
          </button>
        </div>
      )}

      {/* Content */}
      <div className="max-h-[400px] overflow-auto">
        {activeTab === "preview" && artifactType === "html" && (
          <iframe
            srcDoc={content}
            sandbox="allow-scripts allow-same-origin"
            className="h-[300px] w-full border-0"
            title={title}
          />
        )}
        {activeTab === "preview" && artifactType === "svg" && (
          <div
            className="flex items-center justify-center p-4"
            dangerouslySetInnerHTML={{ __html: sanitizeSvg(content) }}
          />
        )}
        {activeTab === "preview" && artifactType === "markdown" && (
          <div className="p-3">
            <MessageResponse>{content}</MessageResponse>
          </div>
        )}
        {(activeTab === "source" || !hasPreview) && (
          <div className="p-3">
            <MessageResponse>
              {"```" + (language || artifactType) + "\n" + content + "\n```"}
            </MessageResponse>
          </div>
        )}
      </div>
    </div>
  );
}
